import { MouseEvent, useRef, useState } from "react";
import { BiSolidXCircle } from "react-icons/bi";
import { SimpleImageBox } from "views/components/CardBodyBoxes/ImageBox";
import ImageUploadModal from "../../ImageDropBox/ImageUploadModal";
import { useImageUpload } from "hooks/useImageUpload";
import styles from "./style.module.scss";
import VisualSimilarityPopover from "views/components/VisualSimilarityPopover";
import { useSelector } from "react-redux";
import { getIsSearchByRelevanceEnabled } from "redux/SingleDataset/selectors";
import useEntityTypeFromQuery from "hooks/useEntityTypeFromQuery";

export interface ImageFileCardProps {
  filename: string;
  imageSrc: string;
  imageThumbSrc: string;
  cardElementRef: any;
  onRemoveVertex: (e: MouseEvent) => void;
  onSetVertexSimilarity?: (threshold: string) => void;
}

//temporary added the flag to disable visual similarity threshold feature
const isVisualSimilarityEnabled = false;

export const ImageFileCard = ({
  filename,
  imageSrc,
  imageThumbSrc,
  cardElementRef,
  onRemoveVertex,
  onSetVertexSimilarity,
}: ImageFileCardProps) => {
  const { file, setFile, setCropData } = useImageUpload();
  const [isHovering, setIsHovering] = useState(false);
  const entityType = useEntityTypeFromQuery();
  const isSearchByRelevanceEnabled = useSelector((state) =>
    getIsSearchByRelevanceEnabled(state, entityType),
  );

  const title = `Image: ${filename}`;
  const titleRef = useRef<HTMLSpanElement>(null);
  const titleContainerRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleFileUpload = async () => {
    if (!imageSrc) return;

    try {
      const response = await fetch(imageSrc);
      const blob = await response.blob();

      // Create a File object from the blob
      const fileName = imageSrc.split("/").pop() || "image.jpg";
      const newFile = new File([blob], fileName, { type: blob.type });

      setFile(newFile);
    } catch (error) {
      console.error("Error fetching file:", error);
    }
  };

  const handleVisualSimilarityApply = (threshold: string) => {
    if (onSetVertexSimilarity) {
      onSetVertexSimilarity(threshold);
    }
  };

  const handleMouseEnter = (e: any) => {
    setIsHovering(true);
  };
  const handleMouseLeave = (e: any) => {
    setIsHovering(false);
  };

  return (
    <div
      className={styles.singleClusterWrapper}
      ref={cardElementRef}
      id="clusterCardWrapper"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className={styles.dataCard}>
        <div className={styles.cardHead} title={title}>
          <div
            className={styles.cardHeadTitleContainer}
            ref={titleContainerRef}
          >
            <div className={styles.leftSection}>
              <span ref={titleRef} className={styles.titleName}>
                {title}
              </span>
            </div>
            <div className={styles.vertexButton} onClick={onRemoveVertex}>
              Remove <BiSolidXCircle size="16px" />
            </div>
          </div>
        </div>
        <div className={styles.cardBody}>
          <SimpleImageBox image={imageThumbSrc} />
        </div>
        {isHovering &&
          isSearchByRelevanceEnabled &&
          isVisualSimilarityEnabled && (
            <div className={styles.actionButtons}>
              <VisualSimilarityPopover onApply={handleVisualSimilarityApply} />
            </div>
          )}
      </div>
      {!!file && (
        <ImageUploadModal
          file={file}
          setFile={setFile}
          setCropData={setCropData}
        />
      )}
    </div>
  );
};
